/** Precast wall panel and railwork that divide the fattening pens. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone } from '@/render/palette';

function fatteningSurfaces(): readonly [number, number, number, number, number] {
  const surfaces = getZone('fattening')?.surfaces;
  if (!surfaces || surfaces.length < 5) throw new Error('fattening zone needs five surface colours');
  return [surfaces[0]!, surfaces[1]!, surfaces[2]!, surfaces[3]!, surfaces[4]!];
}

function box(
  size: readonly [number, number, number],
  colour: number,
  name: string,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), surface('mineral', colour));
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function rail(radius: number, length: number, colour: number, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 6), surface('metal', colour));
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-wall';
  const [shadow, concrete, steel] = fatteningSurfaces();

  // Splitting the casting at the joint makes its dark insert genuinely recessed,
  // rather than a decorative stripe sitting proud of a precast face.
  const lowerPanel = box([0.1, 0.3725, 2.4], concrete, 'precast-panel-lower');
  lowerPanel.position.y = 0.18625;
  const upperPanel = box([0.1, 0.3625, 2.4], concrete, 'precast-panel-upper');
  upperPanel.position.y = 0.56875;
  const castingSeam = box([0.07, 0.015, 2.4], shadow, 'recessed-casting-seam');
  castingSeam.position.y = 0.38;
  group.add(lowerPanel, upperPanel, castingSeam);

  const coping = box([0.12, 0.04, 2.4], concrete, 'proud-coping');
  coping.position.y = 0.73;
  group.add(coping);

  for (const [z, name] of [[-1.14, 'end-post-back'], [1.14, 'end-post-front']] as const) {
    const post = box([0.12, 1.05, 0.12], concrete, name);
    post.position.set(0, 0.525, z);
    group.add(post);
  }

  for (const [y, name] of [[0.9, 'rail-low'], [1.04, 'rail-top']] as const) {
    const horizontal = rail(0.02, 2.4, steel, name);
    horizontal.rotation.x = Math.PI / 2;
    horizontal.position.y = y;
    group.add(horizontal);
  }
  for (const [z, name] of [[-1.14, 'upright-back'], [0, 'upright-centre'], [1.14, 'upright-front']] as const) {
    const upright = rail(0.02, 1.05, steel, name);
    upright.position.set(0, 0.525, z);
    group.add(upright);
  }

  return group;
}

export const penWallAsset: AssetDefinition = {
  id: 'pen-wall',
  label: 'Fattening pen wall',
  description: 'Precast pen wall with a recessed casting joint and galvanised railwork.',
  // 0.12, which is the END POSTS, not the 0.10 m panel they stand proud of.
  //
  // Declared the other way round first, on the reasonable-sounding grounds that
  // adjoining kit aligns to the wall plane rather than to the posts. `asset.spec`
  // rejected it, and the gate is right: `footprint` is what the mesh MEASURES,
  // because it drives the preview's camera framing and the acceptance shots. What
  // the world file should align to is a fact about the world file, and
  // `fattening.ts` keeps it in `PEN_WALL_THICKNESS`.
  footprint: { width: 0.12, height: 1.05, length: 2.4 },
  create: build,
};
